import type { IntegrationConfig } from '../interfaces';

export function getNoEvoRedirectParam(): { noEvoRedirect?: 'true' } {
  const params = new URLSearchParams(window.location.search);

  return params.get('noEvoRedirect') === 'true' ? { noEvoRedirect: 'true' } : {};
}

type EvoIbePreviewMode =
  | 'redirect'
  | 'new-tab'
  | 'modal'
  | 'modal-button'
  | 'fullscreen'
  | 'fullscreen-button';

type EvoIbePreviewConfig = Pick<
  IntegrationConfig,
  'useEvo' | 'panelEnabled' | 'panelFullscreen' | 'panelButtonEnabled' | 'newTabRedirect'
>;

const evoIbePreviewConfigs: Record<EvoIbePreviewMode, EvoIbePreviewConfig> = {
  redirect: {
    useEvo: true,
    panelEnabled: false,
    panelFullscreen: false,
    panelButtonEnabled: false,
    newTabRedirect: false
  },
  'new-tab': {
    useEvo: true,
    panelEnabled: false,
    panelFullscreen: false,
    panelButtonEnabled: false,
    newTabRedirect: true
  },
  modal: {
    useEvo: true,
    panelEnabled: true,
    panelFullscreen: false,
    panelButtonEnabled: false,
    newTabRedirect: false
  },
  'modal-button': {
    useEvo: true,
    panelEnabled: true,
    panelFullscreen: false,
    panelButtonEnabled: true,
    newTabRedirect: false
  },
  fullscreen: {
    useEvo: true,
    panelEnabled: true,
    panelFullscreen: true,
    panelButtonEnabled: false,
    newTabRedirect: false
  },
  'fullscreen-button': {
    useEvo: true,
    panelEnabled: true,
    panelFullscreen: true,
    panelButtonEnabled: true,
    newTabRedirect: false
  }
};

export function getEvoIbePreviewConfig(search: string): EvoIbePreviewConfig | undefined {
  const preview = new URLSearchParams(search).get('evoIbePreview');

  if (!preview || !Object.prototype.hasOwnProperty.call(evoIbePreviewConfigs, preview)) {
    return undefined;
  }

  return { ...evoIbePreviewConfigs[preview as EvoIbePreviewMode] };
}

export function removeQueryParam(urlString: string, paramToRemove: string) {
  const url = new URL(urlString);
  url.searchParams.delete(paramToRemove);
  return url.toString();
}

export function removeQueryParamPreservingEncoding(
  urlString: string,
  paramToRemove: string
): string {
  const hashIndex = urlString.indexOf('#');
  const hash = hashIndex >= 0 ? urlString.slice(hashIndex) : '';
  const urlWithoutHash = hashIndex >= 0 ? urlString.slice(0, hashIndex) : urlString;
  const queryIndex = urlWithoutHash.indexOf('?');
  if (queryIndex < 0) return urlString;

  const baseUrl = urlWithoutHash.slice(0, queryIndex);
  let removed = false;
  const params = urlWithoutHash
    .slice(queryIndex + 1)
    .split('&')
    .filter(param => {
      if (!param) return true;
      const rawName = param.split('=', 1)[0];
      try {
        if (decodeURIComponent(rawName) !== paramToRemove) return true;
      } catch {
        if (rawName !== paramToRemove) return true;
      }
      removed = true;
      return false;
    });
  if (!removed) return urlString;

  const query = params.some(Boolean) ? `?${params.join('&')}` : '';
  return `${baseUrl}${query}${hash}`;
}
